import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';

export type ToastSeverity = 'success' | 'info' | 'warning' | 'error';

export interface Toast {
  id: string;
  message: string;
  severity: ToastSeverity;
}

interface UiState {
  /** Desktop-only: the rail collapses to icons. Irrelevant below `md`. */
  sidebarCollapsed: boolean;
  /** Mobile-only: the temporary navigation drawer. */
  navDrawerOpen: boolean;
  toasts: Toast[];
}

const initialState: UiState = {
  sidebarCollapsed: false,
  navDrawerOpen: false,
  toasts: [],
};

/**
 * Client-owned UI state.
 *
 * Note what is NOT here: no API responses, no loading flags, no error strings
 * from requests. RTK Query already tracks all of that. This slice holds only
 * state the server has no opinion about.
 */
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    sidebarToggled(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    navDrawerOpened(state) {
      state.navDrawerOpen = true;
    },
    navDrawerClosed(state) {
      state.navDrawerOpen = false;
    },
    toastShown: {
      reducer(state, action: PayloadAction<Toast>) {
        state.toasts.push(action.payload);
      },
      prepare(message: string, severity: ToastSeverity = 'info') {
        return { payload: { id: nanoid(), message, severity } };
      },
    },
    toastDismissed(state, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter((toast) => toast.id !== action.payload);
    },
  },
  // Slice-local selectors: they resolve against the mount point without
  // importing RootState, which would create an app -> feature -> app cycle.
  selectors: {
    selectSidebarCollapsed: (state) => state.sidebarCollapsed,
    selectNavDrawerOpen: (state) => state.navDrawerOpen,
    selectToasts: (state) => state.toasts,
  },
});

export const { sidebarToggled, navDrawerOpened, navDrawerClosed, toastShown, toastDismissed } =
  uiSlice.actions;

export const { selectSidebarCollapsed, selectNavDrawerOpen, selectToasts } = uiSlice.selectors;

export const uiReducer = uiSlice.reducer;
